import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	CfCaptchaWidget,
	useCfTurnstileChallenge
} from '@/components/CfCaptchaWidget/index.js';
import ErrorMessage from '@/components/form/ErrorMessage';
import Input from '@/components/form/Input';
import { useGetErrorMessage } from '@/hooks';
import Account from '@/icons/account.svg';
import Key from '@/icons/key.svg';
import authModel from '@/models/auth';
import routes from '@/routes';
import { setTriggerAction } from '@/store/editor';
import {
	AUTH_MODAL_TYPES,
	closeAuthModal,
	openAuthModal
} from '@/store/profile';
import { fetchUser } from '@/store/profile/thunks';
import { delay } from '@/utils';

import Description from './components/Description';
import Divider from './components/Divider';
import OAuthButtons from './components/OAuthButtons';
import TextLink from './components/TextLink';
import Title from './components/Title';
import { DEFAULT_REDIRECT_ROUTE } from './components/constants';

function Login() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const getErrorMessage = useGetErrorMessage();
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const isEditor = useMatch({ path: routes.editor, end: false });
	const isCompress = useSelector(({ editor }) => editor.isModalCompressOpen);

	const { authModalScope, authModalRedirect } = useSelector(
		state => state.profile
	);

	const {
		cfChallengeToken,
		cfChallengeCompleted,
		cfIdempotencyKey,
		setCfIdempotencyKey,
		triggerChallenge
	} = useCfTurnstileChallenge({
		action: 'login'
	});

	const schema = Yup.object({
		email: Yup.string().email().required(),
		password: Yup.string().required()
	});
	const methods = useForm({
		resolver: yupResolver(schema),
		mode: 'onBlur',
		defaultValues: {
			email: '',
			password: ''
		}
	});

	const email = methods.watch('email');

	useEffect(() => {
		if (email.includes('@')) {
			triggerChallenge();
		}
	}, [email]);

	async function onSubmit(values) {
		setLoading(true);
		setErrorMessage('');

		try {
			await authModel.login({
				...values,
				cfChallengeToken,
				cfIdempotencyKey
			});

			await dispatch(fetchUser()).unwrap();

			if (isEditor) {
				if (!isCompress) {
					dispatch(setTriggerAction(authModalScope));
				}
			} else {
				navigate(authModalRedirect || DEFAULT_REDIRECT_ROUTE);
			}

			await delay(200);
			dispatch(closeAuthModal());
		} catch (error) {
			if (error?.data?.cfIdempotencyKey) {
				setCfIdempotencyKey(error.data.cfIdempotencyKey);
			}

			if (error.status === 401) {
				return setErrorMessage(t('authModal.invalidCredentials'));
			}

			if (error.status === 403 && error.data?.errorCode === 'user_banned') {
				dispatch(
					openAuthModal({
						type: AUTH_MODAL_TYPES.ACCOUNT_CLOSED
					})
				);
			}

			setErrorMessage(getErrorMessage(error));
		} finally {
			setLoading(false);
		}
	}

	return (
		<>
			<Title>{t('authModal.login.title')}</Title>

			<Description>{t('authModal.login.description')}</Description>

			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<div className="mb-5 mt-4 grid gap-2 md:mt-6 md:gap-4">
						<Input
							StartIcon={Account}
							name="email"
							placeholder={t('authModal.emailPlaceholder')}
						/>
						<Input
							StartIcon={Key}
							name="password"
							type="password"
							placeholder={t('common.password')}
						/>
						<CfCaptchaWidget />
					</div>
					<TextLink
						disabled={loading}
						className="my-5"
						text={t('authModal.login.forgotPassword')}
						linkText={t('authModal.login.clickHere')}
						onClick={() => {
							dispatch(
								openAuthModal({
									type: AUTH_MODAL_TYPES.RECOVER_PASSWORD
								})
							);
						}}
					/>

					<Button
						type="submit"
						className="w-full"
						disabled={!cfChallengeCompleted}
						loading={loading}
					>
						{t('common.login')}
					</Button>

					{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

					<Divider className="my-2 md:my-5" />

					<OAuthButtons
						type="login"
						authTrigger={
							authModalScope ? `${authModalScope}-login` : null
						}
						fastCheckout={isEditor}
					/>
				</form>
				<TextLink
					disabled={loading}
					text={t('authModal.login.noAccount')}
					linkText={t('authModal.createAccount')}
					onClick={() => {
						dispatch(
							openAuthModal({
								type: AUTH_MODAL_TYPES.SIGN_UP
							})
						);
					}}
				/>
			</FormProvider>
		</>
	);
}

export default Login;
